import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { JawType, TreatmentStage } from '../../../types/admin';
import { useCreateCycleAlignerSets } from '../hooks/useTreatmentOps';
import styles from './StepPanel.module.scss';
import { JawProgressSection } from './WearingPanel/JawProgressSection';
import type { AlignerSet } from './WearingPanel/buildPatch';

const DEFAULT_DAYS = '7';
const SUCCESS_MESSAGE_MS = 2500;

type WearingPanelProps = {
  readonly patientId: string;
  readonly treatmentId: string;
  readonly stage: TreatmentStage;
};

export function WearingPanel({ patientId, treatmentId, stage }: WearingPanelProps) {
  const { t, i18n } = useTranslation();
  const mutation = useCreateCycleAlignerSets(patientId, treatmentId, stage.id);
  const pendingJaw: JawType | undefined = mutation.isPending ? mutation.variables?.jaw_type : undefined;

  const [successJaw, setSuccessJaw] = useState<JawType | null>(null);
  const [errorJaw, setErrorJaw] = useState<JawType | null>(null);
  const successTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => () => {
    if (successTimerRef.current) clearTimeout(successTimerRef.current);
  }, []);

  const wearingStarted = stage.wearing_started_at != null;

  function handleSubmit(jaw: JawType, count: number, targetDays: number | undefined, hasExisting: boolean) {
    setErrorJaw(null);
    setSuccessJaw(null);
    if (successTimerRef.current) clearTimeout(successTimerRef.current);

    mutation.mutate(
      {
        jaw_type: jaw,
        count,
        target_wear_days: targetDays,
        replace: hasExisting,
      },
      {
        onSuccess: () => {
          setSuccessJaw(jaw);
          successTimerRef.current = setTimeout(() => setSuccessJaw(null), SUCCESS_MESSAGE_MS);
        },
        onError: () => setErrorJaw(jaw),
      },
    );
  }

  const alignerSets = stage.aligner_sets ?? [];
  const upperAligners = useMemo(
    () => alignerSets.filter((a) => a.jaw_type === 'upper').slice().sort(byAlignerNumber),
    [alignerSets],
  );
  const lowerAligners = useMemo(
    () => alignerSets.filter((a) => a.jaw_type === 'lower').slice().sort(byAlignerNumber),
    [alignerSets],
  );

  // Current days-per-aligner is the target_wear_days shared by existing sets.
  // Surfacing it lets JawRow detect a days-only change (count unchanged).
  const upperDays = currentTargetDays(upperAligners);
  const lowerDays = currentTargetDays(lowerAligners);

  const hasProgress = upperAligners.length > 0 || lowerAligners.length > 0;

  return (
    <div className={styles.panel}>
      <header className={styles.wearingHeader}>
        <div className={styles.wearingHeaderText}>
          <h4 className={styles.title}>{t('wearing.title')}</h4>
          <p className={styles.subtitle}>{t('wearing.subtitle')}</p>
        </div>
        {wearingStarted && (
          <span className={styles.startedChip}>
            {t('wearing.started')}
            <span className={styles.startedChipValue}>
              {new Date(stage.wearing_started_at!).toLocaleDateString(i18n.language)}
            </span>
          </span>
        )}
      </header>

      <div className={styles.jawGrid}>
        <JawRow
          jaw="upper"
          currentCount={stage.upper_aligners_count ?? null}
          currentDays={upperDays}
          wearingStarted={wearingStarted}
          isPending={pendingJaw === 'upper'}
          showSuccess={successJaw === 'upper'}
          errorMessage={
            errorJaw === 'upper' && mutation.error instanceof Error ? mutation.error.message : null
          }
          onSubmit={(count, days, hasExisting) => handleSubmit('upper', count, days, hasExisting)}
        />
        <JawRow
          jaw="lower"
          currentCount={stage.lower_aligners_count ?? null}
          currentDays={lowerDays}
          wearingStarted={wearingStarted}
          isPending={pendingJaw === 'lower'}
          showSuccess={successJaw === 'lower'}
          errorMessage={
            errorJaw === 'lower' && mutation.error instanceof Error ? mutation.error.message : null
          }
          onSubmit={(count, days, hasExisting) => handleSubmit('lower', count, days, hasExisting)}
        />
      </div>

      {hasProgress && (
        <>
          <hr className={styles.divider} />

          <h4 className={styles.progressSectionTitle}>{t('wearing.progressTitle')}</h4>

          <JawProgressSection
            jaw="upper"
            patientId={patientId}
            treatmentId={treatmentId}
            cycleId={stage.id}
            aligners={upperAligners}
          />
          <JawProgressSection
            jaw="lower"
            patientId={patientId}
            treatmentId={treatmentId}
            cycleId={stage.id}
            aligners={lowerAligners}
          />
        </>
      )}
    </div>
  );
}

export default WearingPanel;

function byAlignerNumber(a: AlignerSet, b: AlignerSet): number {
  return a.aligner_number - b.aligner_number;
}

/** Days-per-aligner currently configured for a jaw, taken from its sets. */
function currentTargetDays(aligners: readonly AlignerSet[]): number | null {
  return aligners[0]?.target_wear_days ?? null;
}

type JawRowProps = {
  readonly jaw: JawType;
  readonly currentCount: number | null;
  readonly currentDays: number | null;
  readonly wearingStarted: boolean;
  readonly isPending: boolean;
  readonly showSuccess: boolean;
  readonly errorMessage: string | null;
  readonly onSubmit: (count: number, targetDays: number | undefined, hasExisting: boolean) => void;
};

function JawRow({
  jaw,
  currentCount,
  currentDays,
  wearingStarted,
  isPending,
  showSuccess,
  errorMessage,
  onSubmit,
}: JawRowProps) {
  const { t } = useTranslation();
  const hasExisting = currentCount != null && currentCount > 0;

  // Baseline days is the value the field is seeded with: the stored
  // target_wear_days when known, otherwise the default. Comparing edits against
  // this baseline (not against a possibly-null currentDays) lets a days-only
  // change register as dirty even when the existing sets carry no stored target.
  const baselineDays = currentDays ?? Number.parseInt(DEFAULT_DAYS, 10);

  const [count, setCount] = useState(hasExisting ? String(currentCount) : '');
  const [days, setDays] = useState(String(baselineDays));

  useEffect(() => {
    setCount(currentCount != null ? String(currentCount) : '');
  }, [currentCount]);

  useEffect(() => {
    setDays(String(baselineDays));
  }, [baselineDays]);

  const parsedCount = parseInt(count, 10);
  const validCount = Number.isFinite(parsedCount) && parsedCount >= 1 && parsedCount <= 100;
  const parsedDays = parseInt(days, 10);
  const validDays = Number.isFinite(parsedDays) && parsedDays >= 1 && parsedDays <= 30;

  // "Unchanged" only when BOTH count and days match what was loaded, so an
  // admin editing days-per-aligner alone can still submit.
  const countMatches = validCount && parsedCount === currentCount;
  const daysMatch = validDays && parsedDays === baselineDays;
  const isUnchanged = hasExisting && countMatches && daysMatch;

  const lockedByWearing = wearingStarted && hasExisting;
  const buttonLabel = isPending
    ? t('wearing.creating')
    : hasExisting
    ? t('wearing.updateSets')
    : t('wearing.createSets');
  const disabled = isPending || !validCount || !validDays || isUnchanged || lockedByWearing;

  // One note line under the fields, highest-priority message wins, so the card
  // keeps a fixed shape instead of growing a stack of hints.
  const note = resolveNote({
    errorMessage,
    lockedByWearing,
    showSuccess,
    isUnchanged,
    isPending,
    estimatedDays: validCount && validDays ? parsedCount * parsedDays : null,
    t,
  });

  function handleClick() {
    if (!validCount || !validDays) return;
    onSubmit(parsedCount, parsedDays, hasExisting);
  }

  return (
    <section className={styles.jawCard}>
      <div className={styles.jawCardHeader}>
        <span className={styles.jawCardTitle}>
          {jaw === 'upper' ? t('wearing.upperJaw') : t('wearing.lowerJaw')}
        </span>
        <span
          className={
            hasExisting
              ? styles.jawCardChip
              : `${styles.jawCardChip} ${styles.jawCardChipEmpty}`
          }
        >
          {hasExisting
            ? t('wearing.alignerCountChip', { total: currentCount })
            : t('wearing.notConfigured')}
        </span>
      </div>

      <div className={styles.jawCardFields}>
        <label className={styles.field}>
          <span>{t('wearing.numberOfAligners')}</span>
          <input
            type="number"
            value={count}
            onChange={(e) => setCount(e.target.value)}
            min="1"
            max="100"
            placeholder="14"
            disabled={lockedByWearing}
          />
        </label>
        <label className={styles.field}>
          <span>{t('wearing.daysPerAligner')}</span>
          <input
            type="number"
            value={days}
            onChange={(e) => setDays(e.target.value)}
            min="1"
            max="30"
            placeholder="7"
            disabled={lockedByWearing}
          />
        </label>
      </div>

      <div className={styles.jawCardFooter}>
        <p className={`${styles.jawCardNote} ${note.className}`} role={note.isError ? 'alert' : undefined}>
          {note.text}
        </p>
        <button
          type="button"
          className={styles.primaryBtn}
          disabled={disabled}
          onClick={handleClick}
        >
          {buttonLabel}
        </button>
      </div>
    </section>
  );
}

type NoteInput = {
  readonly errorMessage: string | null;
  readonly lockedByWearing: boolean;
  readonly showSuccess: boolean;
  readonly isUnchanged: boolean;
  readonly isPending: boolean;
  readonly estimatedDays: number | null;
  readonly t: (key: string, options?: Record<string, unknown>) => string;
};

type Note = {
  readonly text: string;
  readonly className: string;
  readonly isError: boolean;
};

function resolveNote({
  errorMessage,
  lockedByWearing,
  showSuccess,
  isUnchanged,
  isPending,
  estimatedDays,
  t,
}: NoteInput): Note {
  if (errorMessage) {
    return { text: errorMessage, className: styles.jawCardNoteError, isError: true };
  }
  if (showSuccess) {
    return { text: t('wearing.savedJustNow'), className: styles.jawCardNoteSuccess, isError: false };
  }
  if (lockedByWearing) {
    return { text: t('wearing.cannotEditStarted'), className: '', isError: false };
  }
  if (isUnchanged && !isPending) {
    return { text: t('wearing.sameValueHint'), className: '', isError: false };
  }
  if (estimatedDays != null) {
    return {
      text: t('wearing.estimatedWear', { days: estimatedDays }),
      className: '',
      isError: false,
    };
  }
  return { text: '', className: '', isError: false };
}
